/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
// redux
import { useSelector, useDispatch } from 'react-redux';
import {setProductFilter} from "../../../../state/slices/revenue_overview";
import {
    fetchPublisherPageviewForStackedCharts,
    fetchPublisherRevenueForStackedCharts
} from "../../../../state/thunks/revenue_overview";
// material
import {ExportLink, SingleSelectDropDown} from '@insticator/insticator-ui';
import LoadingState from '../../../../material/states/loading/loading';
import NoDataState from '../../../../material/states/nodata/nodata';
import LinkControls from '../../../../material/cardwrapper/linkcontrols/linkcontrols';
import CardWrapper from '../../../../material/cardwrapper/cardwrapper';
import ChartTable from './charttable/charttable';
import StackedCharts from './stackedcharts/stackedcharts';
// utils
import {
    formatTableDate,
    formatNumber,
    formatNumberNoRoundingOff,
    formatXAxisDates,
    getChartCardRateDirection,
    getEnabledRollUps,
    getProductDetails,
    downloadCSV,
    chartTableColors
} from '../../../../utils/charts';
import {getDevices} from "../../../../utils/filterhelpers";
// styles
import injectSheet from 'react-jss';
import styles from './revenuedrivers_styles';
// static
import {AD_UNIT, CEU_CMT, PAGEVIEW_TYPE, PRODUCT_TYPE, VIDEO} from "../../monetization/consts";
import {getStartAndEndDatesOfWeek} from "../../../../utils/date";

const chartColors = {
    revenue: chartTableColors[0],
    rpm: chartTableColors[5],
    pageview: chartTableColors[6]
}

/* ========== ~~~~~~~~~~ REVENUE DRIVERS ~~~~~~~~~~ ========== */
const RevenueDrivers = injectSheet(styles)((props) => {
    const { details, status, overview } = useSelector((state) => state.revenueOverview.stackedCharts);
    const { filters } = useSelector((state) => state.app);
    const { sites, dateRange, devices, compareTo, countries } = useSelector((state) => state.app.filters);

    const [currentChartValues, setCurrentChartValues] = useState({revenue: 0, rpm: 0, pageview: 0});
    const [selectedPeriod, setSelectedPeriod] = useState('daily');
    const period = `${selectedPeriod[0].toUpperCase()}${selectedPeriod.slice(1)}`; // Daily, Monthly, Weekly, Quarterly
    const [periodText, setPeriodText] = useState('');
    const [showTotal, setShowTotal] = useState(true);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const currentTotalDetail =  Object.keys(details['TOTAL'][period].TOTAL);
    const productList = [
        {name: 'All', value: 'ALL'},
        {name: 'Engagement Products', value: 'ENGAGEMENT_PRODUCTS'},
        {name: 'Full Ad Management', value: 'FULL_AD_MANAGEMENT'},
        {name: 'Video', value: 'VIDEO'},
        {name: 'Supply Side Platform', value: 'PREBID_ADAPTER'},
    ]
    const [currentProducts, setCurrentProducts] = useState('ALL');
    // actions
    const dispatch = useDispatch();

    useEffect(() => {
        if(showTotal) {
            const chartValues = {
                revenue: 0,
                rpm: 0,
                pageview: 0,
                revenueChangeRate: 0,
                rpmChangeRate: 0,
                pageviewChangeRate: 0
            }

            setCurrentChartValues({
                revenue: overview.TOTAL.revenue ? overview.TOTAL.revenue : 0,
                rpm: overview.TOTAL.rpm ? overview.TOTAL.rpm : 0,
                pageview: overview.TOTAL.pageview ? overview.TOTAL.pageview : 0,
                revenueChangeRate: overview.TOTAL.revenueChangeRate ? overview.TOTAL.revenueChangeRate : 0,
                rpmChangeRate: overview.TOTAL.rpmChangeRate ? overview.TOTAL.rpmChangeRate : 0,
                pageviewChangeRate: overview.TOTAL.pageviewChangeRate ? overview.TOTAL.pageviewChangeRate : 0
            })

            setPeriodText('');
        }
    }, [overview, showTotal, currentProducts]);

    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details['TOTAL']);
            setEnabledRollups(rollups);
            if(rollups.length > 0) setSelectedPeriod(rollups[0]);
        } else {
            setEnabledRollups([]);
            setShowTotal(true);
            setSelectedPeriod('daily');
        }
    }, [status]);

    useEffect(() => {
        dispatch(setProductFilter([]));
    }, [sites, dateRange, devices, compareTo, countries])

    const revenueDrivers = [
        { id: 'revenue', color: chartColors.revenue, title: 'Revenue'},
        { id: 'rpm', color: chartColors.rpm, title: 'Avg RPM'},
        { id: 'pageview', color: chartColors.pageview,  title: 'Pageviews'}
    ];

    const revenueDriversForSSP = [
        { id: 'revenue', color: chartColors.revenue, title: 'Revenue'}
    ];

    const convertWeeklyDateToStartAndEnd = (date) => {
        if (period.toUpperCase() === 'WEEKLY') {
            const dateArr = date.split('-');
            const year = dateArr[0];
            const week = dateArr[1];
            return getStartAndEndDatesOfWeek(week, year);
        } else return ({startDate: null, endDate: null});
    }

    const formatStackedChartData = (details, period) => {
        const detailsTotalData = details.TOTAL[period].TOTAL;
        const dateLength = Object.keys(detailsTotalData).length;

        const datasets = currentProducts === 'Supply Side Platform' ? [
            { id: 'revenue', data: [{id: 'revenue', color: chartColors.revenue, data: []}], color: chartColors.revenue, title: 'Revenue'},
        ] : [
            { id: 'revenue', data: [{id: 'revenue', color: chartColors.revenue, data: []}], color: chartColors.revenue, title: 'Revenue'},
            { id: 'rpm', data: [{id: 'rpm', color: chartColors.rpm, data: []}], color: chartColors.rpm, title: 'Avg RPM'},
            { id: 'pageview', data: [{id: 'pageview', color: chartColors.pageview, data: []}], color: chartColors.pageview,  title: 'Pageviews'}
        ];

        Object.keys(detailsTotalData).forEach((date, index) => {
            datasets.forEach(dataset => {
                let spaces = '';
                for(var i = 0; i < index; i++) spaces += ' ';
                const { startDate, endDate } = convertWeeklyDateToStartAndEnd(date);
                dataset.data[0].data.push({
                    x: formatXAxisDates(date, period, dateLength, 10).concat(spaces),
                    y: detailsTotalData[date][dataset.id],
                    tableDate: formatTableDate(date, period, startDate, endDate),
                    origDate: date,
                    [`${dataset.id}ChangeRate`]: detailsTotalData[date][`${dataset.id}ChangeRate`]
                });
            })
        });
        return datasets;
    }

    const handleChartMouseMove = (data, period) => {
        setShowTotal(false);
        setCurrentChartValues(data);
        setPeriodText(period);
    }

    const controlOptions = [
        {"text":"Daily","value":"daily","disabled": !enabledRollups.includes('daily')},
        {"text":"Weekly","value":"weekly","disabled": !enabledRollups.includes('weekly')},
        {"text":"Monthly","value":"monthly","disabled": !enabledRollups.includes('monthly')},
        {"text":"Quarterly","value":"quarterly","disabled": !enabledRollups.includes('quarterly')}
    ];

    const isNoData = () => {
        return  (status === 'rejected') ||
            (status === 'fulfilled' && currentTotalDetail.length === 0) ||
            (status === 'fulfilled' && overview['TOTAL'].revenue === 0 && overview['TOTAL'].rpm === 0 && overview['TOTAL'].pageview === 0);
    }
    const isShowData = () => status === 'fulfilled' && !isNoData();
    const isLoading = () => status === 'pending';

    const stackedChartData = formatStackedChartData(details, period);

    const convertDataToCSV = chartData => {
        let csvData = 'Period,Revenue,RPM,Pageview';
        let fileName = 'Revenue Drivers Data';

        const dates = chartData[0].data[0].data.map(chartData => chartData.x);
        const origDates = chartData[0].data[0].data.map(chartData => chartData.origDate);
        csvData += "\n";

        dates.forEach((date, index) => {
            const productDetailsPerPeriod = details['TOTAL'][period]['TOTAL'];
            const origDate = origDates[index];

            const revenueCell = formatNumberNoRoundingOff(productDetailsPerPeriod[origDate].revenue, 'dollar').replaceAll(',', ''); //setting rounding to false to download exact precision with no rounding offs
            const calculateRpm = (productDetailsPerPeriod[origDate].pageview === 0 ? productDetailsPerPeriod[origDate].rpm : (productDetailsPerPeriod[origDate].revenue * 1000)/productDetailsPerPeriod[origDate].pageview)
            const rpmCell = formatNumberNoRoundingOff(calculateRpm, 'dollar').replaceAll(',', '');//setting rounding to false to download exact precision with no rounding offs
            const pageviewCell = formatNumber(productDetailsPerPeriod[origDate].pageview).replaceAll(',', '');

            csvData += `${period == 'Daily' ? origDate : date},${revenueCell},${rpmCell},${pageviewCell}`;
            csvData += "\n";
        })
        // set the file name
        fileName += ` ${period} `;
        fileName = dates.length > 1 ? `${fileName} - ${dates[0]} - ${dates[dates.length - 1]}` : fileName;
        fileName += ` / ${currentProducts}`
        return {
            data: csvData,
            fileName
        }
    }

    const handleExport = () => {
        const csvData = convertDataToCSV(stackedChartData);
        downloadCSV(csvData.data, `${csvData.fileName}.csv`, 'text/csv;encoding:utf-8');
    }

    const handleButtonAction = option => {
        const revenueParams = props.getParams('revenue')
        const pageviewParams = props.getParams('pageview');

        revenueParams.filters[PRODUCT_TYPE] = getProductMapping('revenue', option.value)
        pageviewParams.filters[PAGEVIEW_TYPE] = getProductMapping('pageview', option.value)

        setCurrentProducts(option.name)
        dispatch(fetchPublisherRevenueForStackedCharts(revenueParams));
        dispatch(fetchPublisherPageviewForStackedCharts(pageviewParams));
    }

    const getProductMapping = (type, value) => {
        if (value === 'ALL' || (type === 'pageview' && value === 'PREBID_ADAPTER')) return undefined;
        const mapping = {
            'ENGAGEMENT_PRODUCTS': {
                revenue: ['COMMENT_EMBED', 'QUESTION_EMBED'],
                pageview: [CEU_CMT]
            },
            'FULL_AD_MANAGEMENT': {
                revenue: ['STANDALONE', 'ANCHOR', 'INTERSTITIAL'],
                pageview: [AD_UNIT]
            },
            'VIDEO': {
                revenue: ['IN_STREAM_VIDEO', 'OUT_STREAM_VIDEO', 'VIDEO'],
                pageview: [VIDEO]
            },
            'PREBID_ADAPTER': {
                revenue: ['PREBID_ADAPTER'],
                pageview: []
            }
        }
        return mapping[value][type];
    };

    return (
        <CardWrapper klass={`revenuedrivers ${props.classes.revenuedrivers}`} tooltipContent={props.tooltipContent}>
                <div className={'data'}>
                    <div className={'responsiveline'}>
                        <div className={'driverheader'}>
                            <div className={'title title_date'}>{props.title}</div>
                            <SingleSelectDropDown
                                klass={'productdropdown'}
                                searchable={false}
                                onSelect={handleButtonAction}
                                items={productList}
                                selected={productList.find(p => p.name === currentProducts)}
                                name={currentProducts}
                            />
                        </div>
                        { isLoading() && <LoadingState />}
                        { isNoData() && <NoDataState variant={'noBackground'} title="Revenue Drivers" />}
                        {isShowData() &&
                            <>
                                <div className={'chartcontainer'}>
                                    <StackedCharts
                                        period={period}
                                        datasets={stackedChartData}
                                        onMouseLeave={() => setShowTotal(true)}
                                        onMouseMove={handleChartMouseMove}
                                        reset={showTotal}
                                    />
                                </div>
                                <div className={'rollup'}>
                                    <LinkControls
                                        options={controlOptions}
                                        selected={selectedPeriod}
                                        handleSelect={value => {
                                            setShowTotal(true);
                                            setSelectedPeriod(value);
                                        }}
                                    />
                                </div>
                            </>
                        }
                    </div>
                    {isShowData() &&
                        <div className={'table'}>
                            <div className={'daterange title_date'}>{periodText}</div>
                            {currentProducts === 'Supply Side Platform' ?
                                revenueDriversForSSP.map(chart =>
                                    <ChartTable
                                        title={chart.title}
                                        titleColor={chart.color}
                                        value={formatNumber(currentChartValues[chart.id], chart.id !== 'pageview' ? 'dollar' : '')}
                                        changeDirection={getChartCardRateDirection(currentChartValues[`${chart.id}ChangeRate`])}
                                        change={formatNumber(currentChartValues[`${chart.id}ChangeRate`], 'percent')}
                                        changeType={filters.compareTo}
                                    />
                                ) : revenueDrivers.map(chart =>
                                    <ChartTable
                                        title={chart.title}
                                        titleColor={chart.color}
                                        value={formatNumber(currentChartValues[chart.id], chart.id !== 'pageview' ? 'dollar' : '')}
                                        changeDirection={getChartCardRateDirection(currentChartValues[`${chart.id}ChangeRate`])}
                                        change={formatNumber(currentChartValues[`${chart.id}ChangeRate`], 'percent')}
                                        changeType={filters.compareTo}
                                    />
                                )
                            }
                            <div className={'exportbutton'}>
                                <ExportLink action={handleExport} processing={false}/>
                            </div>
                        </div>
                    }
                </div>
            }
        </CardWrapper>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default RevenueDrivers;
